import { Tabs } from 'antd';
import Page from 'components/Page';
import Table from 'components/Table';
import { DetailField, DetailFields } from 'components/DetailFields';
import React, { useMemo } from 'react';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { allIssueColumns, issueTypeColumns } from './columns';
import { useAccessibilityReview } from './context';
import { HiddenFindingsFilter, InactiveNotice, PageIntroduction, PageTypeFilter, ReviewPage, TABLE_SCROLL } from './ReviewPage';
import { FINDING_SUITES, findingSuite } from './utils';
import { findingEvidenceStatusLabel, findingHeadingLabel, findingRequirementLabel, findingStandardLabel, groupOccurrencesByPage, suiteIssueCount } from './viewModel';
import { hasWcagCriterionHelp, WcagCriteriaDetailField } from './WcagCriterionHelp';

const ISSUE_TYPE_TABLE_SCROLL = { x: 860 };
const ALL_ISSUE_TABLE_SCROLL = { x: 1100 };

const useFindingDispositionColumns = (onView, options) => {
  const { canEditDisposition, dispositionLoadingIds, setFindingDisposition } = useAccessibilityReview();
  return allIssueColumns(onView, {
    ...options,
    canEditDisposition,
    dispositionLoadingIds,
    onDispositionChange: setFindingDisposition,
  });
};

const Findings = () => {
  const { baseUrl, pageType, scope, result, setSelectedOccurrence, shellLoading, findingsLoading } = useAccessibilityReview();
  const history = useHistory();
  const { search } = useLocation();
  const suiteMatch = useRouteMatch(`${baseUrl}/findings/:suitePath`);
  const suitePath = suiteMatch?.params?.suitePath;
  const suiteConfig = FINDING_SUITES.find(suite => suite.path === suitePath) || FINDING_SUITES[0];
  const suite = findingSuite(result, suiteConfig.suiteType);
  const tableLoading = shellLoading || findingsLoading;
  const issueColumns = useFindingDispositionColumns(setSelectedOccurrence, { pageType, suiteType: suite.suiteType });
  const allIssues = useMemo(() => suite.findingGroups.flatMap(group => group.occurrences.map(occurrence => ({
    ...occurrence,
    ruleId: group.ruleId,
    criterionIds: group.criterionIds,
    classification: group.classification,
    status: occurrence.status || group.status,
    suiteType: suite.suiteType,
  }))), [suite]);

  return (
    <ReviewPage title={`${suiteConfig.label} findings`}>
      <Page.Content>
        {!shellLoading && !scope && <InactiveNotice />}
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {suiteConfig.suiteType === 'Accessibility' && <PageTypeFilter style={{ marginBottom: 16 }} />}
          <HiddenFindingsFilter />
        </div>
        <Tabs
          items={[{
            key: 'issue-types',
            label: `Finding types (${suite.findingGroupCount})`,
            children: (
              <Table
                columns={issueTypeColumns(group => history.push(`${baseUrl}/findings/${suiteConfig.path}/${encodeURIComponent(group.id)}${search}`), suite.suiteType)}
                dataSource={suite.findingGroups}
                interactive={false}
                loading={tableLoading}
                pagination={{ pageSize: 25 }}
                resizable={false}
                scroll={ISSUE_TYPE_TABLE_SCROLL}
                tableLayout="fixed"
              />
            ),
          }, {
            key: 'all-issues',
            label: `All issues (${suiteIssueCount(suite)})`,
            children: (
              <Table
                columns={issueColumns}
                dataSource={allIssues}
                loading={tableLoading}
                pagination={{ pageSize: 25 }}
                resizable={false}
                scroll={ALL_ISSUE_TABLE_SCROLL}
                tableLayout="fixed"
              />
            ),
          }]}
        />
      </Page.Content>
    </ReviewPage>
  );
};

export const FindingDetail = () => {
  const { baseUrl, pageType, scope, result, setSelectedOccurrence, shellLoading, findingsLoading } = useAccessibilityReview();
  const { search } = useLocation();
  const findingMatch = useRouteMatch(`${baseUrl}/findings/:suitePath/:findingId`);
  const { suitePath, findingId } = findingMatch?.params || {};
  const suiteConfig = FINDING_SUITES.find(suite => suite.path === suitePath) || FINDING_SUITES[0];
  const suite = findingSuite(result, suiteConfig.suiteType);
  const finding = suite.findingGroups.find(group => group.id === decodeURIComponent(findingId));
  const wcagHelp = hasWcagCriterionHelp({ criterionIds: finding?.criterionIds });
  const findingsPath = `${baseUrl}/findings/${suiteConfig.path}${search}`;
  const tableLoading = shellLoading || findingsLoading;
  const groupedColumns = useFindingDispositionColumns(setSelectedOccurrence, {
    includeIssueType: false,
    pageType,
    suiteType: suite.suiteType,
    groupedByPage: true,
  });
  const emptyColumns = useFindingDispositionColumns(setSelectedOccurrence, {
    includeIssueType: false,
    pageType,
    suiteType: suiteConfig.suiteType,
  });
  const issueRows = useMemo(() => (finding ? groupOccurrencesByPage(finding.occurrences.map(occurrence => ({
    ...occurrence,
    ruleId: finding.ruleId,
    criterionIds: finding.criterionIds,
    classification: finding.classification,
    status: occurrence.status || finding.status,
    suiteType: suite.suiteType,
  }))) : []), [finding, suite.suiteType]);

  return (
    <ReviewPage
      title={finding
        ? `Finding: ${suiteConfig.suiteType === 'Accessibility' ? findingHeadingLabel(finding) : finding.ruleId}`
        : 'Finding'}
    >
      <Page.Content>
        {!shellLoading && !scope ? (
          <>
            <InactiveNotice />
            <Link to={findingsPath}>← Back to findings</Link>
            <Table
              columns={emptyColumns}
              dataSource={[]}
              pagination={{ pageSize: 25 }}
              resizable={false}
              scroll={TABLE_SCROLL}
            />
          </>
        ) : !finding && !tableLoading ? (
          <>
            <Link to={findingsPath}>← Back to findings</Link>
            <PageIntroduction title="Finding not available">
              This finding is no longer part of the latest completed accessibility import.
            </PageIntroduction>
          </>
        ) : !finding ? (
          <>
            <Link to={findingsPath}>← Back to findings</Link>
            <Table
              columns={emptyColumns}
              dataSource={[]}
              loading
              pagination={{ pageSize: 25 }}
              resizable={false}
              scroll={TABLE_SCROLL}
            />
          </>
        ) : (
          <>
            <Link to={findingsPath}>← Back to findings</Link>
            <div style={{ marginTop: 20 }}>
              <DetailFields style={{ marginBottom: 20 }}>
                {suiteConfig.suiteType === 'Accessibility' ? (
                  <>
                    <DetailField label="Standard or category">{findingStandardLabel(finding)}</DetailField>
                    <DetailField label="Requirement / relationship">{findingRequirementLabel(finding)}</DetailField>
                    <DetailField label="Evidence status">{findingEvidenceStatusLabel(finding)}</DetailField>
                    <DetailField label="Provider rule">{finding.ruleId}</DetailField>
                  </>
                ) : <DetailField label="Finding type">{finding.ruleId}</DetailField>}
                <DetailField label="Pages">{finding.pageCount}</DetailField>
              </DetailFields>
              {wcagHelp ? (
                <div style={{ marginBottom: 20 }}>
                  <DetailFields>
                    <WcagCriteriaDetailField criterionIds={finding.criterionIds} />
                  </DetailFields>
                </div>
              ) : finding.description && (
                <div style={{ marginBottom: 20 }}>
                  <DetailFields>
                    <DetailField label="Description">{finding.description}</DetailField>
                  </DetailFields>
                </div>
              )}
              {finding.fixInstructions && (
                <div style={{ marginBottom: 20 }}>
                  <DetailFields>
                    <DetailField label="How to fix">{finding.fixInstructions}</DetailField>
                  </DetailFields>
                </div>
              )}
              {!wcagHelp && finding.helpUrl && (
                <div style={{ marginBottom: 20 }}>
                  <a href={finding.helpUrl} target="_blank" rel="noopener noreferrer">Learn more</a>
                </div>
              )}
              <Table
                columns={groupedColumns}
                dataSource={issueRows}
                pagination={{ pageSize: 25 }}
                resizable={false}
                tableLayout="fixed"
              />
            </div>
          </>
        )}
      </Page.Content>
    </ReviewPage>
  );
};

export default Findings;
